import { Box, useMediaQuery, useTheme } from "@mui/material";
import { FacilitiesExportButton } from "components/FacilitiesExportButton";
import { Importer } from "components/Importer";
import { ImportDropdown } from "components/Importer/ImportDropdown";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { FacilitiesQuery, FacilityStatus } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

type Row = FacilitiesQuery["facilities"]["items"][number];

const FACILITIES = gql(`
  query Facilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        customerFacilityId
        streetAddress1
        city
        county
        state
        zip
        tenantId
        status
        tags {
          id
          name
        }
      }
      count
    }
  }
`);

export const FacilitiesTable: React.FC = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const columns: OmnisearchGridColDef<Row>[] = useMemo(() => {
    return [
      {
        field: "facility",
        headerName: "Facility",
        flex: isMobile ? 0.7 : 0.5,
        filterKeyType: "facility",
        valueGetter: (_, row) => {
          const { name, customerFacilityId } = row;

          return `${name}${customerFacilityId ? ` (${customerFacilityId})` : ""}`;
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: isMobile ? 0.28 : 0.18,
        minWidth: 120,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(FacilityStatus).sort(),
        renderCell: ({ row }) => (row.status ? prettyPrintEnumValue(row.status) : ""),
      },
      {
        field: "state",
        headerName: "State/Territory",
        flex: isMobile ? 0.3 : 0.125,
        minWidth: isMobile ? 80 : undefined,
      },
      {
        field: "county",
        headerName: "County",
        flex: 0.25,
      },
      {
        field: "streetAddress1",
        headerName: "Street Address",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          if (!row.streetAddress1) return "";

          const { streetAddress1, city, state, zip } = row;

          return [streetAddress1, city, state, zip].filter((s) => s).join(", ");
        },
      },
      {
        field: "tags",
        headerName: "Tags",
        flex: 0.3,
        sortable: false,
        filterKeyType: "facilityTags",
        renderCell: ({ row }) => <TagsDisplay tags={row.tags} />,
      },
    ];
  }, [isMobile]);

  useBreadcrumb([
    {
      label: "Facilities",
      to: `/o/${tenantId}/facilities`,
    },
  ]);

  const renderImportExportButtons = useCallback(
    ({ search }: { search: string }) => {
      const buttons = [
        <FacilitiesExportButton
          key="export"
          tenantId={tenantId}
          filter={search ? { search } : undefined}
        />,
      ];
      if (isStaff) {
        buttons.unshift(
          <ImportDropdown
            key="import"
            importers={[
              {
                name: "Facilities",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.Facility}
                    refetchQueries={["Facilities"]}
                  />
                ),
              },
              {
                name: "Facility Alternate IDs",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.FacilityAlternateId}
                    refetchQueries={["Facilities"]}
                  />
                ),
              },
            ]}
          />
        );
      }
      return buttons;
    },
    [isStaff, tenantId]
  );

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Facility Info",
        fields: ["facility", "customerFacilityId", "status"],
      },
      {
        name: "Location",
        fields: ["state", "county", "city", "zip", "streetAddress1"],
      },
      {
        name: "Tags",
        fields: ["tags"],
      },
    ],
    []
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="chemicals-facilities-table"
        isURLDriven
        columns={columns}
        dataQuery={FACILITIES}
        initialSortModel={[
          { field: "state", sort: "asc" },
          { field: "facility", sort: "asc" },
        ]}
        defaultSearch={`tenantId:${tenantId}`}
        getItems={(data) => data.facilities.items}
        getCount={(data) => data.facilities.count}
        noDataMessage="No facilities have been added to this organization yet."
        onRowClick={({ row }) => navigate(row.id)}
        commandButtons={renderImportExportButtons}
        initialPageSize={50}
        additionalFilterColumns={[
          { header: "Facility ID", key: "customerFacilityId" },
          { header: "City", key: "city" },
          { header: "Zip", key: "zip" },
        ]}
        filterGroups={filterGroups}
        keyAliases={{
          tags: "facilityTags",
        }}
        tenantId={tenantId}
        columnVisibilityModel={{
          ...(isMobile && {
            county: false,
            streetAddress1: false,
            tags: false,
            actions: false,
          }),
        }}
      />
    </Box>
  );
};
